import gql from 'graphql-tag';

import type { EventKind } from 'common/components/NotificationsWidget/EventKind';
import EntityContext from 'common/constants/entityContexts';
import type { PageInfoResponse } from 'common/graphql/fragments/PageInfo';
import { isNullOrUndefined } from 'common/helpers/utils';
import type { AccountKind } from 'common/types/accountKind';

export const TaskFragment = gql`
  fragment Task on Task {
    id
    displayName
    kind
    status: stage
    startTime: insertedAt
    endTime: finishedAt
    duration: durationSec
    insertedAt
    updatedAt
    externalResourceId

    result: resultInfo {
      kind
      description
      meta {
        url
        arguments
        additionalDetails {
          description
          reason
          arguments
        }
      }
    }

    progress: progressInfo {
      total
      current
      messages {
        level
        message
      }
    }

    initiatedBy: startedBy {
      id
      firstName
      lastName
      identityProvider
      email
    }

    canceledBy {
      id
      firstName
      lastName
      identityProvider
      email
    }

    account {
      id
      name
      kind
    }
    organization {
      id
      name
    }
    customer {
      id
      name
    }

    args
  }
`;

export enum TaskStatus {
  SUCCEEDING = 'SUCCEEDING',
  NOT_STARTED = 'NOT_STARTED',
  FAILING = 'FAILING',
  FAILED = 'FAILED',
  DONE = 'DONE',
  RUNNING = 'RUNNING',
  CANCELLED = 'CANCELLED',
  CANCELLING = 'CANCELLING',
}

export enum TaskKind {
  CHECK_REQUIRED_NETWORK_RESOURCES = 'CHECK_REQUIRED_NETWORK_RESOURCES',
  SCALE_OUT_CLUSTER_FGW = 'SCALE_OUT_CLUSTER_FGW',
  UPGRADE_FGW_VERSION = 'UPGRADE_FGW_VERSION',
  UPGRADE_FGW_BGP_VERSION = 'UPGRADE_FGW_BGP_VERSION',
  UPDATE_FGW_INSTANCE_TYPE = 'UPDATE_FGW_INSTANCE_TYPE',
  REPLACE_FGW_SSH_KEY = 'REPLACE_FGW_SSH_KEY',
  DEACTIVATE_ACCOUNT = 'DEACTIVATE_ACCOUNT',
  VALIDATE_CLOUD_SERVICE = 'VALIDATE_CLOUD_SERVICE',
  CREATE_CLOUD_SERVICE = 'CREATE_CLOUD_SERVICE',
  UPDATE_CLOUD_SERVICE_CREDENTIALS = 'UPDATE_CLOUD_SERVICE_CREDENTIALS',
  SWITCH_AVAILABILITY_ZONE = 'SWITCH_AVAILABILITY_ZONE',
  ADD_DATACENTERS_TO_CLOUD_SERVICE = 'ADD_DATACENTERS_TO_CLOUD_SERVICE',
  CONFIGURE_CLOUD_SERVICE = 'CONFIGURE_CLOUD_SERVICE',
  CREATE_CLUSTER = 'CREATE_CLUSTER',
  HIBERNATE_CLUSTER = 'HIBERNATE_CLUSTER',
  RESUME_CLUSTER = 'RESUME_CLUSTER',
  DEACTIVATE_CLUSTER = 'DEACTIVATE_CLUSTER',
  UPDATE_ACCESS_POLICY = 'UPDATE_ACCESS_POLICY',
  UPDATE_CLUSTER_LICENCING_INFO = 'UPDATE_CLUSTER_LICENCING_INFO',
  UPDATE_CLUSTER_KEY_NAME = 'UPDATE_CLUSTER_KEY_NAME',
  VALIDATE_CLUSTER_VPC = 'VALIDATE_CLUSTER_VPC',
  CONDEMN_NODE = 'CONDEMN_NODE',
  UPDATE_CLUSTER_CAPACITY = 'UPDATE_CLUSTER_CAPACITY',
  OPEN_SUPPORT_TUNNEL = 'OPEN_SUPPORT_TUNNEL',
  EXTEND_SUPPORT_TUNNEL = 'EXTEND_SUPPORT_TUNNEL',
  CLOSE_SUPPORT_TUNNEL = 'CLOSE_SUPPORT_TUNNEL',
  DEPLOY_CLUSTER_MASTER_IMAGE = 'DEPLOY_CLUSTER_MASTER_IMAGE',
  TRIGGER_FETCHING_OF_AHV_CLUSTER_RESOURCES = 'TRIGGER_FETCHING_OF_AHV_CLUSTER_RESOURCES',
  ADD_INSTANCE_TYPE = 'ADD_INSTANCE_TYPE',
  REMOVE_INSTANCE_TYPE = 'REMOVE_INSTANCE_TYPE',
  CHECK_CLUSTER_CREATE_LIMITS = 'CHECK_CLUSTER_CREATE_LIMITS',
  CHECK_CLUSTER_CAPACITY_CHANGE_LIMITS = 'CHECK_CLUSTER_CAPACITY_CHANGE_LIMITS',
  START_CLUSTER_RECOVERY = 'START_CLUSTER_RECOVERY',
  UPDATE_CLUSTER_RESOURCE_TAGS = 'UPDATE_CLUSTER_RESOURCE_TAGS',
  UPDATE_CLUSTER_PLACEMENT_GROUP_STRATEGY = 'UPDATE_CLUSTER_PLACEMENT_GROUP_STRATEGY',
  PUBLISH_MANIFEST = 'PUBLISH_MANIFEST',
  PUBLISH_CLOUD_SERVICE_PUBLIC_KEY = 'PUBLISH_CLOUD_SERVICE_PUBLIC_KEY',
  ADD_CLOUD_APPLIANCE_TO_CLOUD_SERVICE = 'ADD_CLOUD_APPLIANCE_TO_CLOUD_SERVICE',
  UPDATE_CLUSTER_AOS_TIER = 'UPDATE_CLUSTER_AOS_TIER',
}

export type TaskEntityDetails = {
  __typename: string;
  id: string;
  name: string;
};

export type TaskUserDetails = {
  __typename: string;
  id: string;
  firstName: string;
  lastName: string;
  identityProvider: string;
  email: string;
};

export type TaskResultAdditionalDetail = {
  description?: string | null;
  reason?: string | null;
  arguments?: Record<string, unknown> | null;
};

export type TaskResult = {
  __typename: string;
  kind: EventKind;
  description?: string | null;
  meta: {
    __typename: string;
    url?: string;
    arguments?: Record<string, unknown> | null;
    additionalDetails?: TaskResultAdditionalDetail[] | null;
  };
};

export type TaskProgressMessage = {
  __typename: string;
  level: string;
  message: string;
};

export type TaskProgress = {
  __typename: string;
  total: number;
  current: number;
  messages: TaskProgressMessage[];
};

export interface TaskResponse {
  __typename: string;
  id: string;
  displayName: string;
  kind: TaskKind;
  status: TaskStatus;
  startTime: string;
  endTime: string | null;
  duration: number;
  result?: TaskResult;
  progress?: TaskProgress;
  externalResourceId?: string;
  initiatedBy?: TaskUserDetails;
  canceledBy?: TaskUserDetails;
  account?: TaskEntityDetails & { kind?: AccountKind };
  organization?: TaskEntityDetails;
  customer?: TaskEntityDetails;
  insertedAt: string;
  updatedAt: string;
  args?: string;
}

export interface TasksResponse {
  owner: {
    id: string;
    tasksPaginated: {
      tasks: TaskResponse[];
      pageInfo: PageInfoResponse;
    };
  };
}

export function getEntityIdFromTask(task: TaskResponse): string | undefined {
  if (task.account) {
    return task.account.id;
  }
  if (task.organization) {
    return task.organization.id;
  }
  return task.customer?.id;
}

export function getEntityContextFromTask(task: TaskResponse): EntityContext | undefined {
  if (task.account) {
    return EntityContext.ACCOUNT;
  }
  if (task.organization) {
    return EntityContext.ORGANIZATION;
  }
  if (task.customer) {
    return EntityContext.CUSTOMER;
  }
  return undefined;
}

/**
 * Gets the account id from the task args if they exist. Returns undefined otherwise.
 * Neccessary because of https://github.com/mainframe2/cpanel/issues/329.
 * account.id from the task will be always undefined as a consequence.
 *
 * @param task The task to get the account id from.
 */
export function getAccountIdFromTaskArgs(task: TaskResponse): string | undefined {
  if (isNullOrUndefined(task.args)) {
    return undefined;
  }

  return JSON.parse(task.args).account_id;
}
